import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import type { Api, BaseQueryFn } from '@reduxjs/toolkit/query';

// Slices and APIs are owned by their feature. They are imported from their
// concrete module paths (not the feature barrels) so the store never pulls a
// feature's React components into the boot chain.
import authReducer, {
  initializeAuth,
  setCredentials,
  logout,
} from '@features/auth/store/authSlice';
import { authApi } from '@features/auth/api/authApi';
import organizationReducer, {
  initializeActiveOrganization,
  setActiveOrganization,
  clearActiveOrganization,
  resetRejectedOrganizations,
} from '@features/organization/store/organizationSlice';
import { organizationApi } from '@features/organization/api/organizationApi';
import companyReducer, {
  initializeActiveCompany,
  setActiveCompany,
  clearActiveCompany,
} from '@features/company/store/companySlice';
import { companyApi } from '@features/company/api/companyApi';
import { insurersApi } from '@features/cards/api/insurersApi';
import { reconciliationApi } from '@features/reconciliation/api/reconciliationApi';
import { tariffsApi } from '@features/tariffs/api/tariffsApi';
import { portfolioApi } from '@features/portfolio/api/portfolioApi';
import { billingApi } from '@features/billing/api/billingApi';
import { auditApi } from '@features/audit/api/auditApi';
import { teamApi } from '@features/team/api/teamApi';
import { claimsApi } from '@features/claims/api/claimsApi';
import { integrationsApi } from '@features/integrations/api/integrationsApi';

/** Element type of {@link TENANT_SCOPED_APIS} and {@link COMPANY_SCOPED_APIS}. */
type TenantScopedApi = Api<BaseQueryFn, Record<string, never>, string, string>;

/**
 * Tenant-scoped data APIs whose caches must be wiped when the active
 * organization changes (so a tenant never sees another tenant's cached rows).
 *
 * `authApi` and `organizationApi` are intentionally EXCLUDED: they are
 * user-scoped (the current user / the organizations they may switch into), not
 * tenant-scoped.
 *
 * `companyApi` lists the tenant's companies, `insurersApi` caches this centre's status
 * for the insurer, `reconciliationApi` its settlements and their lines,
 * `tariffsApi` its contracted prices and the links to its own services,
 * `portfolioApi` those services and their portfolios, `billingApi` what the
 * counter may bill, `claimsApi` what it billed, `teamApi` who holds a seat in
 * this organization, and `integrationsApi` which software reaches it. Every
 * future org-scoped API MUST be added here too.
 *
 * Each concrete API is cast to {@link TenantScopedApi} on the way in: an RTK
 * Query `Api` specialised with real endpoints is not structurally assignable to
 * the erased `Record<string, never>` endpoints type, but only
 * `util.resetApiState()` (which is endpoint-agnostic) is ever called on these.
 */
const TENANT_SCOPED_APIS: TenantScopedApi[] = [
  companyApi as unknown as TenantScopedApi,
  insurersApi as unknown as TenantScopedApi,
  reconciliationApi as unknown as TenantScopedApi,
  tariffsApi as unknown as TenantScopedApi,
  portfolioApi as unknown as TenantScopedApi,
  billingApi as unknown as TenantScopedApi,
  teamApi as unknown as TenantScopedApi,
  auditApi as unknown as TenantScopedApi,
  claimsApi as unknown as TenantScopedApi,
  integrationsApi as unknown as TenantScopedApi,
];

/**
 * The subset of {@link TENANT_SCOPED_APIS} whose rows are also filtered by the
 * active company (`X-Company`, ADR-0018): the statements and activity of a company, the
 * tariffs it contracted, the links to its services, the services themselves,
 * what the counter may bill and what it billed. Wiped when
 * the company changes so the new company never reads the previous one's rows out of the
 * cache. `companyApi` itself is NOT here: the list of companies is the same whichever one
 * is active.
 */
const COMPANY_SCOPED_APIS: TenantScopedApi[] = [
  reconciliationApi as unknown as TenantScopedApi,
  tariffsApi as unknown as TenantScopedApi,
  portfolioApi as unknown as TenantScopedApi,
  billingApi as unknown as TenantScopedApi,
  claimsApi as unknown as TenantScopedApi,
];

/**
 * Listener middleware for cross-slice side effects (cache resets).
 *
 * Persistence is NOT here: both slices write their own localStorage entries in
 * their reducers, so mounting a slice is all it takes for it to survive a
 * reload. This middleware only owns the effects that span *several* slices.
 */
const listenerMiddleware = createListenerMiddleware();

/**
 * Reset everything scoped to the *current user* — the active-organization
 * selection, the 403-rejection list, the switchable-organizations list
 * (`organizationApi`), and every tenant-scoped data API. Called on BOTH login and
 * logout so that switching accounts in the same browser tab never lets one user
 * inherit another's active organization or see another's cached list. (Security:
 * the backend already 403s cross-tenant access; this prevents even stale NAMES from
 * showing.)
 *
 * `authApi` is the deliberate exclusion: its only cached endpoint is `me`, which
 * describes the caller rather than a tenant, and `setCredentials`/`logout` already
 * replace the auth slice that every consumer reads.
 */
const resetForUserChange = (dispatch: AppDispatch) => {
  dispatch(clearActiveCompany());
  dispatch(clearActiveOrganization());
  dispatch(resetRejectedOrganizations());
  dispatch(organizationApi.util.resetApiState());
  for (const tenantApi of TENANT_SCOPED_APIS) {
    dispatch(tenantApi.util.resetApiState());
  }
};

// A new session starts: drop the previous user's tenant state. The login flow
// selects the new active organization AFTER this runs.
listenerMiddleware.startListening({
  actionCreator: setCredentials,
  effect: (_action, api) => resetForUserChange(api.dispatch as AppDispatch),
});

// The session ends: wipe the user- and tenant-scoped caches (see the note above
// on why `authApi` is excluded).
listenerMiddleware.startListening({
  actionCreator: logout,
  effect: (_action, api) => resetForUserChange(api.dispatch as AppDispatch),
});

// Switching tenant: drop the company first (a company belongs to ONE organization, so
// the old uuid would be a forged header on the new tenant), then reset the
// tenant-scoped caches so the new organization never reads the previous one's
// rows out of the cache.
listenerMiddleware.startListening({
  actionCreator: setActiveOrganization,
  effect: (_action, api) => {
    api.dispatch(clearActiveCompany());
    for (const tenantApi of TENANT_SCOPED_APIS) {
      api.dispatch(tenantApi.util.resetApiState());
    }
  },
});

// Switching company: only the company-scoped caches go; the tenant-level ones (insurers,
// settlements, the company list itself) are the same for every company.
listenerMiddleware.startListening({
  actionCreator: setActiveCompany,
  effect: (_action, api) => {
    for (const companyScopedApi of COMPANY_SCOPED_APIS) {
      api.dispatch(companyScopedApi.util.resetApiState());
    }
  },
});

// The active company was dropped, which happens for two reasons: the tenant changed,
// or the backend refused the `X-Company` header (`core/api/baseQuery`). The second one
// is what a revoked grant looks like from here, and the switcher is still showing
// the company nobody may open any more, so the granted list has to be read again.
listenerMiddleware.startListening({
  actionCreator: clearActiveCompany,
  effect: (_action, api) => {
    api.dispatch(companyApi.util.invalidateTags(['Company']));
  },
});

// Moving services between portfolios changes which insurers they are billed to,
// which the tariffs section reads as links and coverage. The two features never
// import each other, so the store owns the invalidation.
listenerMiddleware.startListening({
  matcher: portfolioApi.endpoints.assignScope.matchFulfilled,
  effect: (_action, api) => {
    api.dispatch(tariffsApi.util.invalidateTags(['Link', 'Coverage']));
  },
});

/**
 * Redux store configuration.
 */
export const store = configureStore({
  reducer: {
    auth: authReducer,
    organization: organizationReducer,
    company: companyReducer,
    [authApi.reducerPath]: authApi.reducer,
    [organizationApi.reducerPath]: organizationApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [insurersApi.reducerPath]: insurersApi.reducer,
    [reconciliationApi.reducerPath]: reconciliationApi.reducer,
    [tariffsApi.reducerPath]: tariffsApi.reducer,
    [portfolioApi.reducerPath]: portfolioApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [teamApi.reducerPath]: teamApi.reducer,
    [auditApi.reducerPath]: auditApi.reducer,
    [claimsApi.reducerPath]: claimsApi.reducer,
    [integrationsApi.reducerPath]: integrationsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(listenerMiddleware.middleware)
      .concat(authApi.middleware)
      .concat(organizationApi.middleware)
      .concat(companyApi.middleware)
      .concat(insurersApi.middleware)
      .concat(reconciliationApi.middleware)
      .concat(tariffsApi.middleware)
      .concat(portfolioApi.middleware)
      .concat(billingApi.middleware)
      .concat(teamApi.middleware)
      .concat(auditApi.middleware)
      .concat(claimsApi.middleware)
      .concat(integrationsApi.middleware),
  devTools: import.meta.env.DEV,
});

// Re-read the persisted session on app start. Both slices already rehydrate at
// module load (so the first render knows whether there is a session); this
// covers storage written after the module was evaluated, e.g. by another tab.
store.dispatch(initializeAuth());
store.dispatch(initializeActiveOrganization());
store.dispatch(initializeActiveCompany());

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
